import React, { useState } from 'react';
import { Chart } from 'react-google-charts';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

import Arrow from 'components/elements/Arrow';
import { SelectGroupMappingDropdown } from 'components/form/SelectGroupMappingDropdown';

import Colors from 'themes/themeColors';

import {
  getInnerDisplayFirstRowForNewNemar,
  getInnerDisplaySecondRowForNewNemar,
	getOuterDisplayConfig,
  getChartQtyForNewNemar,
  chartOptionsForNewNemar,
	LoanSummary,
	displayTurnaroundTime,
} from '../cardStatusDisplayConfig';

type Props = {
	loanSummary: LoanSummary;
	groupMappings: any[];
	groupSelections: any[];
	setGroupSelections: (val: any[]) => void;
};

const LoanChartForNewNemar = ({
	loanSummary,
	groupMappings,
	groupSelections,
	setGroupSelections,
}: Props) => {
	const [isGroupMappingsDropdownOpen, setIsGroupMappingsDropdownOpen] =
		useState<boolean>(false);

	return (
		<div
			style={{
				marginTop: '16px',
				marginBottom: '5%',
				borderTop: '1px solid #E7E9ED',
			}}>
			<DropdownContainer>
				<HeaderText>Applications Status
				<TurnAroundTimeText>
							Turnaround Time:{" "}
							{loanSummary?.turnaround_time
								? displayTurnaroundTime(loanSummary.turnaround_time)
								: "-"}
						</TurnAroundTimeText>
				</HeaderText>
				<SelectGroupMappingDropdown
					data={groupMappings}
					selections={groupSelections}
					setSelections={setGroupSelections}
					width='23%'
					isOpen={isGroupMappingsDropdownOpen}
					setIsOpen={setIsGroupMappingsDropdownOpen}
					direction='right'
				/>
			</DropdownContainer>
			<div
				style={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					width: '100%',
				}}>
				{getOuterDisplayConfig(loanSummary).map((d, idx) => {
					return (
						<StatusContainer key={idx.toString()}>
							<Link
								to={{
									pathname: d.pathname,
									state: {
										applicationstatus: d.applicationstatus,
										groupSelections,
									},
								}}>
								<ApplicationStatusWrapper>
									<ApplicationStatus>{d.label} </ApplicationStatus>
									<Arrow border={2} length={6} color='#2536b2' />
								</ApplicationStatusWrapper>
								<ApplicationCount>{d.qty}</ApplicationCount>
							</Link>
						</StatusContainer>
					);
				})}
			</div>
			<ChartContainer>
				<div
					style={{
						width: '25%',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
					}}>
					<Chart
						chartType='PieChart'
						options={chartOptionsForNewNemar}
						data={getChartQtyForNewNemar(loanSummary)}
						height={'300px'}
					/>
				</div>
				<ChartHeadingWrapper>
					<ChartHeading>
						{getInnerDisplayFirstRowForNewNemar(loanSummary).map((d, idx) => {
							return (
								<Link
									key={idx.toString()}
									to={{
										pathname: d.pathname,
										state: {
											applicationstatus: d.applicationstatus,
											groupSelections,
										},
									}}
									style={{
										textDecoration: 'none',
										width: '25%',
										paddingBottom: '24px',
										borderBottom: '1px solid #E7E9ED',
									}}>
									<ApplicationStatusWrapper>
										<img src={d.img} alt='refresh' width={17} height={17} />
										<CountTitle>{d.label}</CountTitle>
										<Arrow border={2} length={6} color='#3548C9' />
									</ApplicationStatusWrapper>
									<CountText>{d.qty}</CountText>
								</Link>
							);
						})}
					</ChartHeading>
					<ChartHeading>
						{getInnerDisplaySecondRowForNewNemar(loanSummary).map((d, idx) => {
							return (
								<Link
									key={idx.toString()}
									to={{
										pathname: d.pathname,
										state: {
											applicationstatus: d.applicationstatus,
											groupSelections,
										},
									}}
									style={{
										textDecoration: 'none',
										width: '25%',
										paddingTop: '24px',
									}}>
									<ApplicationStatusWrapper>
										<img src={d.img} alt='refresh' width={17} height={17} />
										<CountTitle>{d.label}</CountTitle>
										<Arrow border={2} length={6} color='#3548C9' />
									</ApplicationStatusWrapper>
									<CountText>{d.qty}</CountText>
								</Link>
							);
						})}
					</ChartHeading>
				</ChartHeadingWrapper>
			</ChartContainer>
		</div>
	);
};

const DropdownContainer = styled.div`
	margin-top: 10px;
	margin-bottom: 10px;
	width: 100%;
	display: flex;
	flex-direction: row;
	align-items: center;
	justify-content: space-between;
`;

const HeaderText = styled.div`
	font-weight: 700;
	line-height: 22px;
	text-align: left;
	vertical-align: middle;
	color: #253858;
`;

const ChartContainer = styled.div`
	display: flex;
	width: 100%;
	height: 25vh;
	padding: 24px;
	border-radius: 8px;
	background: #f2f3ff;
	margin-right: 1%;
	margin-top: 16px;
	background: ${Colors.White};
	@media only screen and (max-width: 1688px) {
		height: 27vh;
	}
`;

const ChartHeadingWrapper = styled.div`
	width: 75%;
	display: flex;
	flex-direction: column;
	justify-content: center;
	z-index: 90;
`;

const ChartHeading = styled.div`
	width: 100%;
	display: flex;
	flex-direction: row;
	align-items: center;
	text-allign: center;
	font-size: 14px;
	font-style: normal;
	font-weight: 400;
	line-height: 18px;
`;

const StatusContainer = styled.div`
	display: flex;
	flex-direction: column;
	text-align: left;
	align-items: flex-start;
	border-radius: 8px;
	background: ${Colors.White};
	height: 96px;
	padding: 8px 16px;
	width: 23.5%;
	justify-content: center;
`;

const ApplicationStatusWrapper = styled.div`
	display: flex;
	flex-direction: row;
	align-items: center;
`;

const ApplicationStatus = styled.div`
	margin-right: 3px;
	color: #2536b2;
	font-family: Noto Sans;
	font-size: 12px;
	font-style: normal;
	font-weight: 700;
`;

const ApplicationCount = styled.div`
	color: #395278;
	font-family: Noto Sans;
	font-size: 28px;
	font-style: normal;
	font-weight: 700;
	line-height: 39.2px;
`;

const CountTitle = styled.span`
	margin-left: 4px;
	margin-right: 4px;
	color: #3548c9;
	font-family: Noto Sans;
	font-size: 12px;
	font-style: normal;
	font-weight: 700;
`;

const CountText = styled.h3`
	padding-left: 2px;
	font-family: Noto Sans;
	font-size: 36px;
	font-style: normal;
	font-weight: 700;
	line-height: 50px;
	color: #395278;
`;

const TurnAroundTimeText = styled.div`
	font-family: Inter;
	font-size: 12px;
	line-height: 17px;
	text-align: left;
	font-weight: 400;
	margin-top: 5px;
	vertical-align: middle;
	color: #566a89;
	:hover {
		cursor: default;
	}
`;

export default LoanChartForNewNemar;
